import React from 'react';
import { OASISSchema, OASISFormData, OASISNavigationItem } from './types';
import { ACTIVE_DIAGNOSIS_CATEGORY } from './OASISForm';

interface OASISNavigationProps {
  categories: string[];
  currentCategory: string | null;
  onCategoryChange: (category: string) => void;
  schema: OASISSchema;
  formData: OASISFormData;
  touchedCategories?: string[] | Set<string>;
  errorCountByCategory?: Record<string, number>;
}

export const OASISNavigation: React.FC<OASISNavigationProps> = ({
  categories,
  currentCategory,
  onCategoryChange,
  schema,
  formData,
  touchedCategories = [],
  errorCountByCategory = {},
}) => {
  const touchedCategorySet =
    touchedCategories instanceof Set
      ? touchedCategories
      : new Set(touchedCategories);

  // Calculate completion status for each category (excluding pseudo questions)
  const getNavigationItems = (): OASISNavigationItem[] => {
    return categories.map(category => {
      // Special hardcoded section — no schema questions to count
      if (category === ACTIVE_DIAGNOSIS_CATEGORY) {
        return {
          category,
          label: 'Active Diagnosis',
          isCompleted: false,
          questionCount: 0,
          answeredCount: 0,
        };
      }

      const questions = schema[category] || [];
      const nonPseudoQuestions = questions.filter(q => q.is_pseudo !== true);
      const questionCount = nonPseudoQuestions.length;
      let answeredCount = 0;

      nonPseudoQuestions.forEach(question => {
        const answer = formData[question.qid];
        if (answer !== undefined && answer !== null && answer !== '') {
          if (Array.isArray(answer)) {
            if (answer.length > 0) answeredCount++;
          } else {
            answeredCount++;
          }
        }
      });

      return {
        category,
        label: category
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim(),
        isCompleted: answeredCount === questionCount && questionCount > 0,
        questionCount,
        answeredCount,
      };
    });
  };

  const navigationItems = getNavigationItems();

  return (
    <div className='space-y-0.5'>
      {navigationItems.map(item => {
        const isActive = currentCategory === item.category;
        const isTouched = touchedCategorySet.has(item.category);
        return (
          <button
            key={item.category}
            onClick={() => onCategoryChange(item.category)}
            className={`w-full text-left px-2.5 py-2 rounded-md text-xs leading-snug transition-colors flex items-center justify-between gap-2 ${
              isActive
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <span className='flex-1 min-w-0'>{item.label}</span>

            <span className='flex items-center gap-1.5 self-center overflow-visible'>
              {(errorCountByCategory[item.category] ?? 0) > 0 ? (
                <div
                  className='w-1.5 h-1.5 rounded-full shrink-0 bg-destructive'
                  title='Has live scrubber issues'
                />
              ) : isTouched ? (
                <div
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-primary/70' : 'bg-primary/40'}`}
                  title='Has entered answers'
                />
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
};
